const FILE_PATH_EXTENSION_PATTERN =
  /\.(?:ts|tsx|js|jsx|mjs|cjs|py|go|rs|java|kt|rb|php|cs|swift|vue|svelte|md|json|yaml|yml)$/i;

const INFERRED_SYSTEM_PROMPT_MARKERS = [
  'System prompt not accessible',
  'System instructions paraphrased',
  'paraphrased from runtime behavior',
  'Based on observed UI strings',
  'Based on observed',
  'partially revealed during investigation',
  'includes: role definition as',
] as const;

const AGENT_COMPONENT_TYPES = new Set(['AGENT', 'SUB_AGENT']);

function toMetadataRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
}

function isInferredSystemPrompt(value: string): boolean {
  const normalized = value.trim();
  if (!normalized) {
    return false;
  }

  return INFERRED_SYSTEM_PROMPT_MARKERS.some((marker) =>
    normalized.includes(marker),
  );
}

export function getComponentSourceFilePath(metadata: unknown): string | null {
  const filePath = toMetadataRecord(metadata).filePath;
  return typeof filePath === 'string' && filePath.trim().length > 0
    ? filePath.trim()
    : null;
}

export function isFilePathLikeDescription(
  value: string | null | undefined,
): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.includes('/') || trimmed.includes('\\')) {
    return FILE_PATH_EXTENSION_PATTERN.test(trimmed);
  }

  return false;
}

export function summarizeSystemPromptAsDescription(
  systemPrompt: string,
): string | null {
  const normalized = systemPrompt.trim();
  if (!normalized || isInferredSystemPrompt(normalized)) {
    return null;
  }

  const withoutHeaders = normalized
    .replace(/^#+\s+/gm, '')
    .replace(/<\/?[^>]+>/g, ' ');

  const firstParagraph = withoutHeaders.split(/\n\s*\n/)[0]?.trim() ?? '';
  const candidate = firstParagraph.replace(/\s+/g, ' ').trim();
  if (!candidate) {
    return null;
  }

  if (candidate.length <= 280) {
    return candidate;
  }

  return `${candidate.slice(0, 277).trimEnd()}...`;
}

export function deriveComponentDescriptionFromMetadata({
  type,
  metadata,
}: {
  type: string;
  metadata: unknown;
}): string | null {
  const metadataRecord = toMetadataRecord(metadata);
  const metadataDescription =
    typeof metadataRecord.description === 'string'
      ? metadataRecord.description.trim()
      : '';

  if (metadataDescription && !isFilePathLikeDescription(metadataDescription)) {
    return metadataDescription;
  }

  const systemPrompt =
    typeof metadataRecord.systemPrompt === 'string'
      ? metadataRecord.systemPrompt.trim()
      : '';

  if (!systemPrompt) {
    return null;
  }

  if (AGENT_COMPONENT_TYPES.has(type)) {
    return summarizeSystemPromptAsDescription(systemPrompt);
  }

  return null;
}

export function shouldReplaceComponentDescription({
  existingDescription,
}: {
  existingDescription: string | null | undefined;
}): boolean {
  const trimmed = existingDescription?.trim();
  if (!trimmed) {
    return true;
  }

  return isFilePathLikeDescription(trimmed);
}

export function resolveComponentDescriptionAfterMetadataUpdate({
  existingDescription,
  type,
  metadata,
}: {
  existingDescription: string | null | undefined;
  type: string;
  metadata: unknown;
}): string | null {
  if (!shouldReplaceComponentDescription({ existingDescription })) {
    return existingDescription?.trim() ?? null;
  }

  return deriveComponentDescriptionFromMetadata({ type, metadata });
}

export function resolveAiComponentDisplayDescription({
  description,
  metadata,
  type,
  fallback,
}: {
  description: string | null | undefined;
  metadata: unknown;
  type: string;
  fallback?: string | null;
}): string | null {
  const trimmedDescription = description?.trim();
  if (trimmedDescription && !isFilePathLikeDescription(trimmedDescription)) {
    return trimmedDescription;
  }

  const derived = deriveComponentDescriptionFromMetadata({ type, metadata });
  if (derived) {
    return derived;
  }

  const fallbackTrimmed = fallback?.trim();
  return fallbackTrimmed && fallbackTrimmed.length > 0 ? fallbackTrimmed : null;
}

export function resolveComponentSourceFilePathForMatching({
  description,
  metadata,
}: {
  description?: string | null;
  metadata?: unknown;
}): string | null {
  const metadataFilePath = getComponentSourceFilePath(metadata);
  if (metadataFilePath && metadataFilePath.length >= 8) {
    return metadataFilePath;
  }

  const trimmedDescription = description?.trim();
  if (
    trimmedDescription &&
    trimmedDescription.length >= 8 &&
    isFilePathLikeDescription(trimmedDescription)
  ) {
    return trimmedDescription;
  }

  return null;
}
